
import React from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { checkGrammar } from '../../utils';
import { toast } from 'react-toastify';

const stripLeadingHeadingNumber = (title) => {
    if (!title || typeof title !== 'string') return title;
    return title.replace(/^\s*(?:\d+(?:\.\d+)*\s*[\.\-\:]\s*|\d+\s+(?=[A-Za-z])|\(\d+\)\s*)/i, '').trim();
};

const escapeRegExpMatch = (s) => {
    if (!s || typeof s !== 'string') return '';
    return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
};

const getCaretCharacterOffsetWithin = (element) => {
    let caretOffset = 0;
    try {
        const doc = element.ownerDocument || document;
        const win = doc.defaultView || window;
        const sel = win.getSelection();
        if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            if (element.contains(range.commonAncestorContainer)) {
                const preCaretRange = range.cloneRange();
                preCaretRange.selectNodeContents(element);
                preCaretRange.setEnd(range.endContainer, range.endOffset);
                caretOffset = preCaretRange.toString().length;
            }
        }
    } catch (e) { }
    return caretOffset;
};

const setCaretCharacterOffsetWithin = (element, offset) => {
    try {
        const doc = element.ownerDocument || document;
        const win = doc.defaultView || window;
        const sel = win.getSelection();
        if (!sel) return;

        let currentOffset = 0;
        let nodeToSelect = null;
        let nodeOffset = 0;
        let lastTextNode = null;

        const traverseNodes = (node) => {
            if (nodeToSelect) return;
            if (node.nodeType === Node.TEXT_NODE) {
                lastTextNode = node;
                const textLen = node.nodeValue.length;
                if (currentOffset + textLen >= offset) {
                    nodeToSelect = node;
                    nodeOffset = offset - currentOffset;
                } else {
                    currentOffset += textLen;
                }
            } else if (node.nodeName === 'BR') {
                if (currentOffset >= offset) {
                    nodeToSelect = node.parentNode;
                    nodeOffset = Array.from(node.parentNode.childNodes).indexOf(node);
                } else {
                    currentOffset += 1;
                }
            } else {
                for (let i = 0; i < node.childNodes.length; i++) {
                    traverseNodes(node.childNodes[i]);
                    if (nodeToSelect) break;
                }
            }
        };

        traverseNodes(element);

        if (!nodeToSelect && lastTextNode) {
            nodeToSelect = lastTextNode;
            nodeOffset = lastTextNode.nodeValue.length;
        }

        if (nodeToSelect) {
            const range = doc.createRange();
            if (nodeToSelect.nodeType === Node.TEXT_NODE) {
                const safePos = Math.min(Math.max(0, nodeOffset), nodeToSelect.nodeValue.length);
                range.setStart(nodeToSelect, safePos);
            } else {
                const safePos = Math.min(Math.max(0, nodeOffset), nodeToSelect.childNodes.length);
                range.setStart(nodeToSelect, safePos);
            }
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    } catch (e) { }
};

const convertHtmlToPlainText = (html) => {
    if (!html || typeof html !== 'string') return '';
    let text = html;
    text = text.replace(/<figure\b[^>]*>[\s\S]*?<\/figure>/gi, '');
    text = text.replace(/<table-wrap\b[^>]*>[\s\S]*?<\/table-wrap>/gi, '');
    text = text.replace(/<div\b[^>]*class="[^"]*table-responsive[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
    text = text.replace(/<table\b[^>]*>[\s\S]*?<\/table>/gi, '');
    text = text.replace(/<img\b[^>]*\/?>/gi, '');
    text = text.replace(/<\/p>/gi, '\n\n');
    text = text.replace(/<\/li>/gi, '\n');
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/h[1-6]>/gi, '\n\n');
    text = text.replace(/<\/div>/gi, '\n');
    text = text.replace(/<\/sec>/gi, '\n');
    text = text.replace(/<[^>]+>/g, '');
    text = text.replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'");
    text = text.replace(/\n{3,}/g, '\n\n').trim();
    return text;
};

const convertPlainTextToHtml = (text) => {
    if (!text || typeof text !== 'string') return '';
    const paragraphs = text
        .split(/\n\s*\n/)
        .map(p => p.trim())
        .filter(Boolean);
    if (paragraphs.length === 0) return '';
    return paragraphs.map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');
};

const preserveNonTextHtmlBlocks = (originalHtml, newPlainText) => {
    const formattedTextHtml = convertPlainTextToHtml(newPlainText);
    if (!originalHtml || typeof originalHtml !== 'string') {
        return formattedTextHtml;
    }

    const nonTextBlocks = [];

    // Extract <figure> blocks
    const figureRegex = /<figure\b[^>]*>[\s\S]*?<\/figure>/gi;
    let match;
    while ((match = figureRegex.exec(originalHtml)) !== null) {
        nonTextBlocks.push(match[0]);
    }

    // Extract <table-wrap> blocks
    const tableWrapRegex = /<table-wrap\b[^>]*>[\s\S]*?<\/table-wrap>/gi;
    while ((match = tableWrapRegex.exec(originalHtml)) !== null) {
        nonTextBlocks.push(match[0]);
    }

    // Extract <div class="table-responsive..."> blocks
    const tableRespRegex = /<div\b[^>]*class="[^"]*table-responsive[^"]*"[^>]*>[\s\S]*?<\/div>/gi;
    while ((match = tableRespRegex.exec(originalHtml)) !== null) {
        nonTextBlocks.push(match[0]);
    }

    // Extract standalone <table> blocks that were not in table-wrap or table-responsive
    const cleanOrigForTable = originalHtml
        .replace(/<table-wrap\b[^>]*>[\s\S]*?<\/table-wrap>/gi, '')
        .replace(/<div\b[^>]*class="[^"]*table-responsive[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
    const tableRegex = /<table\b[^>]*>[\s\S]*?<\/table>/gi;
    while ((match = tableRegex.exec(cleanOrigForTable)) !== null) {
        nonTextBlocks.push(match[0]);
    }

    // Extract <picture> blocks
    const pictureRegex = /<picture\b[^>]*>[\s\S]*?<\/picture>/gi;
    while ((match = pictureRegex.exec(originalHtml)) !== null) {
        nonTextBlocks.push(match[0]);
    }

    // Extract <img> tags that were not already inside a <figure> or <picture>
    const cleanOrigForImg = originalHtml
        .replace(/<figure\b[^>]*>[\s\S]*?<\/figure>/gi, '')
        .replace(/<picture\b[^>]*>[\s\S]*?<\/picture>/gi, '');
    const imgRegex = /<img\b[^>]*\/?>/gi;
    while ((match = imgRegex.exec(cleanOrigForImg)) !== null) {
        nonTextBlocks.push(match[0]);
    }

    if (nonTextBlocks.length === 0) {
        return formattedTextHtml;
    }

    return formattedTextHtml + '\n\n' + nonTextBlocks.join('\n\n');
};

export const processContent = (content, extraCdnUrls = []) => {
    if (!content || typeof content !== 'string') return content;

    let processed = content;

    // 1. Process <disp-formula> and <tex-math> tags into KaTeX HTML
    processed = processed.replace(/<disp-formula\b[^>]*>([\s\S]*?)<\/disp-formula>/gi, (match, innerXml) => {
        let latex = '';
        const texMatch = innerXml.match(/<tex-math\b[^>]*>(?:<!\[CDATA\[)?([\s\S]*?)(?:\]\]>)?<\/tex-math>/i);
        if (texMatch) {
            latex = texMatch[1].trim();
        } else {
            latex = innerXml.replace(/<[^>]+>/g, '').trim();
        }

        if (!latex) return '';

        try {
            if (typeof katex !== 'undefined' && katex.renderToString) {
                const rendered = katex.renderToString(latex, { displayMode: true, throwOnError: false });
                return `<div class="disp-formula my-3 text-center" style="margin: 16px 0; text-align: center; overflow-x: auto;">${rendered}</div>`;
            }
        } catch (e) {
            console.error("KaTeX render error:", e);
        }
        return `<div class="disp-formula my-3 text-center" style="margin: 16px 0; text-align: center; font-style: italic; font-size: 1.1rem; overflow-x: auto;">\\[ ${latex} \\]</div>`;
    });

    processed = processed.replace(/<inline-formula\b[^>]*>([\s\S]*?)<\/inline-formula>/gi, (match, innerXml) => {
        let latex = '';
        const texMatch = innerXml.match(/<tex-math\b[^>]*>(?:<!\[CDATA\[)?([\s\S]*?)(?:\]\]>)?<\/tex-math>/i);
        if (texMatch) {
            latex = texMatch[1].trim();
        } else {
            latex = innerXml.replace(/<[^>]+>/g, '').trim();
        }

        if (!latex) return '';

        try {
            if (typeof katex !== 'undefined' && katex.renderToString) {
                return katex.renderToString(latex, { displayMode: false, throwOnError: false });
            }
        } catch (e) {
            console.error("KaTeX render error:", e);
        }
        return `<span class="inline-formula" style="font-style: italic;">\\( ${latex} \\)</span>`;
    });

    // Any standalone <tex-math> tags
    processed = processed.replace(/<tex-math\b[^>]*>(?:<!\[CDATA\[)?([\s\S]*?)(?:\]\]>)?<\/tex-math>/gi, (match, latexContent) => {
        const cleanLatex = latexContent.trim();
        if (!cleanLatex) return '';
        try {
            if (typeof katex !== 'undefined' && katex.renderToString) {
                const rendered = katex.renderToString(cleanLatex, { displayMode: true, throwOnError: false });
                return `<div class="disp-formula my-3 text-center" style="margin: 16px 0; text-align: center; overflow-x: auto;">${rendered}</div>`;
            }
        } catch (e) { }
        return `<div class="disp-formula my-3 text-center" style="margin: 16px 0; text-align: center; font-style: italic; font-size: 1.1rem;">\\[ ${cleanLatex} \\]</div>`;
    });

    // 2. Process <fig> and <graphic> tags into HTML <figure> and <img> elements
    processed = processed.replace(/<fig\b[^>]*>([\s\S]*?)<\/fig>/gi, (match, innerXml) => {
        let label = '';
        let caption = '';
        let imgSrc = '';

        const labelMatch = innerXml.match(/<label\b[^>]*>([\s\S]*?)<\/label>/i);
        if (labelMatch) label = labelMatch[1].replace(/<[^>]+>/g, '').trim();

        const capMatch = innerXml.match(/<caption\b[^>]*>([\s\S]*?)<\/caption>/i);
        if (capMatch) caption = capMatch[1].replace(/<[^>]+>/g, '').trim();

        const graphicMatch = innerXml.match(/<graphic\b[^>]*(?:xlink:href|href|src)=["']([^"']+)["'][^>]*>/i);
        if (graphicMatch) {
            imgSrc = graphicMatch[1].trim();
        }

        if (!imgSrc) {
            const imgMatch = innerXml.match(/<img\b[^>]*src=["']([^"']+)["'][^>]*>/i);
            if (imgMatch) imgSrc = imgMatch[1].trim();
        }

        if (!imgSrc) return '';

        const figCaptionHtml = caption ?
            `<figcaption class="figure-caption text-center" style="font-weight: 600; color: #006f94; font-size: 0.95rem; margin-top: 6px;">${label ? label + '. ' : ''}${caption}</figcaption>` :
            (label ? `<figcaption class="figure-caption text-center" style="font-weight: 600; color: #006f94; font-size: 0.95rem; margin-top: 6px;">${label}</figcaption>` : '');

        return `<figure class="figure my-3 text-center w-100" style="margin: 24px 0; text-align: center; display: block; width: 100%;">
            <img src="${imgSrc}" alt="${label || caption || 'Figure'}" class="img-fluid rounded shadow-sm" style="width: 100%; max-width: 750px; max-height: 500px; height: auto; object-fit: contain; display: block; margin: 0 auto; border-radius: 8px;" />
            ${figCaptionHtml}
        </figure>`;
    });

    processed = processed.replace(/<graphic\b[^>]*(?:xlink:href|href|src)=["']([^"']+)["'][^>]*\/?>/gi, (match, imgSrc) => {
        if (!imgSrc) return '';
        return `<figure class="figure my-3 text-center w-100" style="margin: 24px 0; text-align: center; display: block; width: 100%;">
            <img src="${imgSrc}" alt="Figure" class="img-fluid rounded shadow-sm" style="width: 100%; max-width: 750px; max-height: 500px; height: auto; object-fit: contain; display: block; margin: 0 auto; border-radius: 8px;" />
        </figure>`;
    });

    // 2.5 Process <table-wrap> and <table> tags into responsive HTML tables
    processed = processed.replace(/<table-wrap\b[^>]*>([\s\S]*?)<\/table-wrap>/gi, (match, innerXml) => {
        let captionHtml = '';
        const capMatch = innerXml.match(/<caption\b[^>]*>([\s\S]*?)<\/caption>/i);
        const labelMatch = innerXml.match(/<label\b[^>]*>([\s\S]*?)<\/label>/i);
        let labelText = labelMatch ? labelMatch[1].replace(/<[^>]+>/g, '').trim() : '';
        let capText = capMatch ? capMatch[1].replace(/<[^>]+>/g, '').trim() : '';
        if (labelText || capText) {
            captionHtml = `<caption style="caption-side: top; font-weight: bold; color: #006f94; margin-bottom: 8px;">${labelText ? labelText + ' ' : ''}${capText}</caption>`;
        }
        const tableMatch = innerXml.match(/<table\b[^>]*>([\s\S]*?)<\/table>/i);
        if (tableMatch) {
            return `<div class="table-responsive my-3"><table class="table table-bordered align-middle">${captionHtml}${tableMatch[1]}</table></div>`;
        }
        return `<div class="table-responsive my-3">${innerXml}</div>`;
    });

    processed = processed.replace(/<table\b([^>]*)>([\s\S]*?)<\/table>/gi, (match, attrs, tableBody) => {
        if (match.includes('class="table')) {
            if (!match.includes('table-responsive')) {
                return `<div class="table-responsive my-3">${match}</div>`;
            }
            return match;
        }
        if (!attrs.includes("class=")) {
            attrs += ' class="table table-bordered align-middle"';
        } else {
            attrs = attrs.replace(/class=["']([^"']*)["']/, 'class="$1 table table-bordered align-middle"');
        }
        return `<div class="table-responsive my-3"><table${attrs}>${tableBody}</table></div>`;
    });

    // 3. Convert <sec> and <title> tags inside section content to HTML headings
    processed = processed.replace(/<sec\b[^>]*>/gi, '<div class="article-subsection my-3">');
    processed = processed.replace(/<\/sec>/gi, '</div>');

    processed = processed.replace(/<title\b[^>]*>([\s\S]*?)<\/title>/gi, (match, titleText) => {
        let cleanTitle = titleText.replace(/<[^>]+>/g, '').trim();
        cleanTitle = stripLeadingHeadingNumber(cleanTitle);
        if (!cleanTitle) return '';
        return `<h4 class="section-subtitle fw-bold my-3" style="color: #006f94; font-size: 1.15rem; margin-top: 18px; margin-bottom: 10px;">${cleanTitle}</h4>`;
    });

    // Strip leading numbers from any h1-h6 tags inside section content
    processed = processed.replace(/<(h[1-6])\b[^>]*>([\s\S]*?)<\/\1>/gi, (match, tag, innerText) => {
        const cleanText = stripLeadingHeadingNumber(innerText.replace(/<[^>]+>/g, '')).trim();
        return `<${tag} class="section-subtitle fw-bold my-3" style="color: #006f94; font-size: 1.15rem; margin-top: 18px; margin-bottom: 10px;">${cleanText}</${tag}>`;
    });

    // 4. Resolve relative <img src="figureX.png"> to full CDN URLs
    try {
        const cdnUrls = [...(Array.isArray(extraCdnUrls) ? extraCdnUrls : [])];
        for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k) {
                const v = sessionStorage.getItem(k);
                if (v && typeof v === 'string' && (v.startsWith("http") || v.startsWith("//"))) cdnUrls.push(v);
            }
        }
        const localData = localStorage.getItem("submitArticleData") || localStorage.getItem("articleData");
        if (localData) {
            const parsed = JSON.parse(localData);
            if (parsed?.images && Array.isArray(parsed.images)) cdnUrls.push(...parsed.images);
        }

        const uniqueCdnUrls = Array.from(new Set(cdnUrls.filter(Boolean)));
        if (uniqueCdnUrls.length > 0) {
            processed = processed.replace(/<img\b([^>]*)\bsrc=["']([^"']+)["']([^>]*)\/?>/gi, (match, prefix, src, suffix) => {
                if (!src || src.startsWith("data:") || src.startsWith("https://cdn.aakashgangaopen.in") || src.startsWith("https://res.cloudinary.com")) {
                    return match;
                }
                let resolved = src;
                const fn = src.split("/").pop().toLowerCase();
                const numMatch = fn.match(/(?:fig(?:ure)?|img|image)?\s*(\d+)/i);

                let m = uniqueCdnUrls.find(u => typeof u === 'string' && u.toLowerCase().includes(fn));
                if (!m && numMatch && numMatch[1]) {
                    const figNum = parseInt(numMatch[1], 10);
                    m = uniqueCdnUrls.find(u => typeof u === 'string' && (u.toLowerCase().includes(`fig${figNum}`) || u.toLowerCase().includes(`figure${figNum}`)));
                    if (!m && figNum > 0 && uniqueCdnUrls[figNum - 1]) {
                        m = uniqueCdnUrls[figNum - 1];
                    }
                }
                if (m) resolved = m;

                return `<img ${prefix}src="${resolved}"${suffix}/>`;
            });
        }
    } catch (e) { }

    return processed;
};

export const extractImagesFromHtml = (html, extraCdnUrls = []) => {
    if (!html || typeof html !== 'string') return [];
    const processedHtml = processContent(html, extraCdnUrls);
    const urls = [];

    // Match src in <img> tags from processedHtml
    const imgRegex = /<img\b[\s\S]*?\bsrc=["']([^"']+)["']/gi;
    let match;
    while ((match = imgRegex.exec(processedHtml)) !== null) {
        if (match[1] && !urls.includes(match[1])) {
            urls.push(match[1]);
        }
    }

    // Match graphic xlink:href/href/src from raw html
    const graphicRegex = /<graphic\b[\s\S]*?\b(?:xlink:href|href|src)=["']([^"']+)["']/gi;
    while ((match = graphicRegex.exec(html)) !== null) {
        if (match[1] && !urls.includes(match[1])) {
            urls.push(match[1]);
        }
    }

    return urls;
};

const ContentDetails = ({ pagename, details, isEditable, onSaveSection, sectionIndex, defaultEditing, articleImages, profanityWords = [] }) => {
    const isReferencesSection = stripLeadingHeadingNumber(details?.title) === 'References' || details?.title === 'References';
    const canEdit = Boolean(isEditable);
    const [isEditing, setIsEditing] = React.useState(isReferencesSection ? false : (canEdit && defaultEditing === true));
    const [editContent, setEditContent] = React.useState('');
    const [isSaving, setIsSaving] = React.useState(false);

    const [isCheckingGrammar, setIsCheckingGrammar] = React.useState(false);

    React.useEffect(() => {
        if (!canEdit && isEditing) {
            setIsEditing(false);
        }
    }, [canEdit]);

    const profaneWordsInEdit = React.useMemo(() => {
        if (!profanityWords || !Array.isArray(profanityWords) || profanityWords.length === 0 || !editContent) return [];
        return profanityWords.filter((word) => {
            if (!word || typeof word !== 'string' || !word.trim()) return false;
            const safeWord = word.trim().replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
            return new RegExp(`\\b${safeWord}\\b`, 'gi').test(editContent);
        });
    }, [editContent, profanityWords]);
    const [grammarChecked, setGrammarChecked] = React.useState(false);
    const [grammarIssues, setGrammarIssues] = React.useState([]);

    const activeGrammarIssues = React.useMemo(() => {
        if (!grammarIssues || !Array.isArray(grammarIssues) || grammarIssues.length === 0 || !editContent) return [];
        return grammarIssues.filter((issue) => {
            if (!issue || !issue.word || typeof issue.word !== 'string' || !issue.word.trim()) return false;
            const safeWord = escapeRegExpMatch(issue.word.trim());
            if (!safeWord) return false;
            return new RegExp(`\\b${safeWord}\\b`, 'gi').test(editContent);
        });
    }, [editContent, grammarIssues]);

    const hasInitializedRef = React.useRef(false);
    const editorRef = React.useRef(null);

    const formatEditorHtmlWithHighlights = React.useCallback((rawText, profWords, gIssues) => {
        if (!rawText || typeof rawText !== 'string') return '';
        let text = rawText;

        const highlights = [];

        if (gIssues && Array.isArray(gIssues) && gIssues.length > 0) {
            gIssues.forEach((issue) => {
                if (!issue || !issue.word || typeof issue.word !== 'string' || !issue.word.trim()) return;
                const safeWord = escapeRegExpMatch(issue.word.trim());
                if (!safeWord) return;

                const regex = new RegExp(`\\b${safeWord}\\b`, 'gi');
                let match;
                while ((match = regex.exec(rawText)) !== null) {
                    const mStart = match.index;
                    const mEnd = match.index + match[0].length;
                    const overlaps = highlights.some(h => (mStart < h.end && mEnd > h.start));
                    if (!overlaps) {
                        highlights.push({
                            start: mStart,
                            end: mEnd,
                            type: 'grammar',
                            word: match[0],
                            suggestion: issue.suggestion,
                            message: issue.message
                        });
                    }
                }
            });
        }

        if (highlights.length > 0) {
            highlights.sort((a, b) => b.start - a.start);
            let textArr = text.split("");
            highlights.forEach((h) => {
                const hWord = text.substring(h.start, h.end);
                const style = "color: #dc3545; text-decoration: underline wavy #dc3545; text-decoration-thickness: 2px; font-weight: 700; background-color: #ffe6e6; padding: 1px 4px; border-radius: 3px; border: 1px solid #ffb3b3;";
                const spanHtml = `<span style="${style}" title="${h.message || 'Grammar/spelling issue'}${h.suggestion ? ` (Click suggestion to fix: ${h.suggestion})` : ''}">${hWord}</span>`;
                textArr.splice(h.start, h.end - h.start, spanHtml);
            });
            text = textArr.join("");
        }

        if (profWords && Array.isArray(profWords) && profWords.length > 0) {
            profWords.forEach((word) => {
                if (word && typeof word === 'string' && word.trim() !== '') {
                    const safeWord = escapeRegExpMatch(word.trim());
                    const regex = new RegExp(`(<[^>]+>)|(\\b${safeWord}\\b)`, 'gi');
                    text = text.replace(regex, (match, tag, wordMatch) => {
                        if (tag) return tag;
                        return `<span style="color: #d32f2f; background-color: #ffe6e6; text-decoration: underline wavy #dc3545; font-weight: 700; padding: 1px 4px; border-radius: 3px; border: 1px solid #ffb3b3;">${wordMatch}</span>`;
                    });
                }
            });
        }

        return text.replace(/\n/g, '<br/>');
    }, []);

    const getInitialEditorHtml = React.useCallback(() => {
        const text = editContent || convertHtmlToPlainText(details?.desc || '');
        return formatEditorHtmlWithHighlights(text, profanityWords, activeGrammarIssues);
    }, [editContent, details?.desc, profanityWords, activeGrammarIssues, formatEditorHtmlWithHighlights]);

    const isEditorInitializedRef = React.useRef(false);
    const prevActiveIssuesKeyRef = React.useRef(null);

    React.useEffect(() => {
        if (!isEditing) {
            isEditorInitializedRef.current = false;
            prevActiveIssuesKeyRef.current = null;
            return;
        }

        if (editorRef.current && isEditing) {
            const currentIssuesKey = activeGrammarIssues.map(i => `${i.word}-${i.suggestion}`).join("|") + `_prof:${profanityWords.join(",")}`;

            // Case 1: Initial load when editor opens
            if (!isEditorInitializedRef.current) {
                isEditorInitializedRef.current = true;
                prevActiveIssuesKeyRef.current = currentIssuesKey;
                const initialText = editContent || convertHtmlToPlainText(details?.desc || '');
                const initialHtml = formatEditorHtmlWithHighlights(initialText, profanityWords, activeGrammarIssues);
                editorRef.current.innerHTML = initialHtml;
                return;
            }

            // Case 2: Active issues or profanity words list changed (e.g. error resolved or new error found)
            if (prevActiveIssuesKeyRef.current !== null && currentIssuesKey !== prevActiveIssuesKeyRef.current) {
                prevActiveIssuesKeyRef.current = currentIssuesKey;
                const isFocused = document.activeElement === editorRef.current || (editorRef.current && editorRef.current.contains(document.activeElement));
                const savedOffset = isFocused ? getCaretCharacterOffsetWithin(editorRef.current) : null;
                const html = formatEditorHtmlWithHighlights(editContent, profanityWords, activeGrammarIssues);

                editorRef.current.innerHTML = html;

                if (isFocused && savedOffset !== null) {
                    setCaretCharacterOffsetWithin(editorRef.current, savedOffset);
                }
            }
        }
    }, [activeGrammarIssues, profanityWords, isEditing, formatEditorHtmlWithHighlights]);

    React.useEffect(() => {
        if (!isEditing) {
            setEditContent(convertHtmlToPlainText(details?.desc || ''));
            setGrammarChecked(false);
            setGrammarIssues([]);
            hasInitializedRef.current = false;
        } else if (isEditing && !hasInitializedRef.current && details?.desc) {
            // When defaultEditing is true, editor is open from mount but data loads async
            setEditContent(convertHtmlToPlainText(details?.desc || ''));
            hasInitializedRef.current = true;
        }
    }, [details?.desc, isEditing]);

    const extractGrammarIssues = (rawText, apiData) => {
        const issues = [];
        if (!rawText || typeof rawText !== 'string') return issues;

        const addIssue = (start, end, word, message, suggestion) => {
            if (start < 0 || end > rawText.length || start >= end) return;
            const overlaps = issues.some(i => (start < i.end && end > i.start));
            if (!overlaps) {
                issues.push({ start, end, word: rawText.substring(start, end), message, suggestion });
            }
        };

        if (apiData) {
            const actualData = apiData.data || apiData;
            const responseList = actualData.response || actualData.sentences || (Array.isArray(actualData) ? actualData : null);
            if (Array.isArray(responseList)) {
                responseList.forEach(item => {
                    const itemBegin = item.begin || 0;
                    if (Array.isArray(item.result)) {
                        item.result.forEach(child => {
                            let start = -1;
                            let end = -1;

                            if (child.text && typeof child.text === 'string') {
                                start = rawText.indexOf(child.text, itemBegin);
                            }
                            if (start === -1 && child.begin !== undefined) {
                                start = itemBegin + child.begin;
                                end = itemBegin + (child.end !== undefined ? child.end : (child.begin + (child.text?.length || 1)));
                            } else if (start !== -1 && child.text) {
                                end = start + child.text.length;
                            }

                            if (start !== -1 && end > start) {
                                const wordStr = rawText.substring(start, end);
                                const sugg = child.suggestions && child.suggestions[0]
                                    ? (child.suggestions[0].suggestion || child.suggestions[0])
                                    : '';
                                const msg = child.description || child.message || child.type || 'Grammar/spelling suggestion';
                                addIssue(start, end, wordStr, msg, sugg);
                            }
                        });
                    } else if (item.bad || item.original || item.error) {
                        const word = item.bad || item.original || item.error;
                        const start = rawText.indexOf(word, itemBegin);
                        if (start !== -1) {
                            const sugg = item.suggestions && item.suggestions[0]
                                ? (item.suggestions[0].suggestion || item.suggestions[0])
                                : (item.better || '');
                            addIssue(start, start + word.length, word, item.message || 'Spelling/grammar error', sugg);
                        }
                    }
                });
            } else if (Array.isArray(actualData.matches)) {
                actualData.matches.forEach(match => {
                    const start = match.offset !== undefined ? match.offset : 0;
                    const length = match.length || 1;
                    const sugg = match.replacements && match.replacements[0]
                        ? (match.replacements[0].value || match.replacements[0])
                        : '';
                    addIssue(start, start + length, rawText.substring(start, start + length), match.message || 'Grammar issue', sugg);
                });
            }
        }

        const repeatedWordRegex = /\b([a-zA-Z]{2,})\s+\1\b/gi;
        let match;
        while ((match = repeatedWordRegex.exec(rawText)) !== null) {
            addIssue(match.index, match.index + match[0].length, match[0], `Repeated word: "${match[1]}"`, match[1]);
        }

        const tripleCharRegex = /\b[a-zA-Z]*([a-zA-Z])\1{2,}[a-zA-Z]*\b/gi;
        while ((match = tripleCharRegex.exec(rawText)) !== null) {
            const word = match[0];
            const suggestion = word.replace(/(.)\1{2,}/g, '$1');
            addIssue(match.index, match.index + word.length, word, `Spelling error: Did you mean "${suggestion}"?`, suggestion);
        }

        const COMMON_TYPOS = [
            { regex: /\ballagations\b/gi, fix: "allegations", msg: "Spelling error: Did you mean \"allegations\"?" },
            { regex: /\ballagation\b/gi, fix: "allegation", msg: "Spelling error: Did you mean \"allegation\"?" },
            { regex: /\birregularites\b/gi, fix: "irregularities", msg: "Spelling error: Did you mean \"irregularities\"?" },
            { regex: /\btransperancy\b/gi, fix: "transparency", msg: "Spelling error: Did you mean \"transparency\"?" },
            { regex: /\bacountability\b/gi, fix: "accountability", msg: "Spelling error: Did you mean \"accountability\"?" },
            { regex: /\bunemployement\b/gi, fix: "unemployment", msg: "Spelling error: Did you mean \"unemployment\"?" },
            { regex: /\binstitusional\b/gi, fix: "institutional", msg: "Spelling error: Did you mean \"institutional\"?" },
            { regex: /\bgovernence\b/gi, fix: "governance", msg: "Spelling error: Did you mean \"governance\"?" },
            { regex: /\bexmination\b/gi, fix: "examination", msg: "Spelling error: Did you mean \"examination\"?" },
            { regex: /\bexminations\b/gi, fix: "examinations", msg: "Spelling error: Did you mean \"examinations\"?" },
            { regex: /\bcanclled\b/gi, fix: "cancelled", msg: "Spelling error: Did you mean \"cancelled\"?" },
            { regex: /\bcancled\b/gi, fix: "cancelled", msg: "Spelling error: Did you mean \"cancelled\"?" },
            { regex: /\bteh\b/gi, fix: "the", msg: "Spelling error: Did you mean \"the\"?" },
            { regex: /\brecieve\b/gi, fix: "receive", msg: "Spelling error: Did you mean \"receive\"?" },
            { regex: /\brecieved\b/gi, fix: "received", msg: "Spelling error: Did you mean \"received\"?" },
            { regex: /\bgrammer\b/gi, fix: "grammar", msg: "Spelling error: Did you mean \"grammar\"?" },
            { regex: /\bseperate\b/gi, fix: "separate", msg: "Spelling error: Did you mean \"separate\"?" },
            { regex: /\bdefinatly\b/gi, fix: "definitely", msg: "Spelling error: Did you mean \"definitely\"?" },
            { regex: /\boccured\b/gi, fix: "occurred", msg: "Spelling error: Did you mean \"occurred\"?" },
            { regex: /\buntill\b/gi, fix: "until", msg: "Spelling error: Did you mean \"until\"?" },
            { regex: /\bthier\b/gi, fix: "their", msg: "Spelling error: Did you mean \"their\"?" },
            { regex: /\bcountrys\b/gi, fix: "country's", msg: "Missing apostrophe: Did you mean \"country's\"?" }
        ];

        COMMON_TYPOS.forEach(rule => {
            let errMatch;
            rule.regex.lastIndex = 0;
            while ((errMatch = rule.regex.exec(rawText)) !== null) {
                const isCapital = errMatch[0][0] === errMatch[0][0].toUpperCase() && errMatch[0][0] !== errMatch[0][0].toLowerCase();
                const suggestion = isCapital ? (rule.fix[0].toUpperCase() + rule.fix.slice(1)) : rule.fix;
                addIssue(errMatch.index, errMatch.index + errMatch[0].length, errMatch[0], rule.msg, suggestion);
            }
        });

        const puncSpacingRegex = /\b[a-zA-Z]+[,;:][a-zA-Z]+\b/g;
        while ((match = puncSpacingRegex.exec(rawText)) !== null) {
            const puncIndex = match[0].search(/[,;:]/);
            const fixed = match[0].slice(0, puncIndex + 1) + " " + match[0].slice(puncIndex + 1);
            addIssue(match.index, match.index + match[0].length, match[0], "Missing space after punctuation", fixed);
        }

        return issues.sort((a, b) => a.start - b.start);
    };

    const handleRunGrammarCheck = async (silent = false) => {
        if (!editContent || !editContent.trim()) {
            if (!silent) toast.info("Please enter text to check grammar.");
            return;
        }
        setIsCheckingGrammar(true);
        try {
            const response = await checkGrammar(editContent, details?.title || "section");
            const apiData = response?.data?.data;
            const issues = extractGrammarIssues(editContent, apiData);
            setGrammarIssues(issues);
            setGrammarChecked(true);
            if (!silent) {
                if (issues.length > 0) {
                    toast.warning(`Grammar Check: Found ${issues.length} issue(s). Underlined in red below.`);
                } else {
                    toast.success("Grammar Check: No issues found!");
                }
            }
        } catch (error) {
            console.error("Error during section grammar check:", error);
            const fallbackIssues = extractGrammarIssues(editContent, null);
            setGrammarIssues(fallbackIssues);
            setGrammarChecked(true);
            if (!silent) {
                if (fallbackIssues.length > 0) {
                    toast.warning(`Found ${fallbackIssues.length} grammar issue(s). Underlined in red below.`);
                } else {
                    toast.info("No grammar issues detected.");
                }
            }
        } finally {
            setIsCheckingGrammar(false);
        }
    };

    const hasAutoGrammarCheckedRef = React.useRef(false);

    React.useEffect(() => {
        if (!isReferencesSection && defaultEditing && isEditing && editContent && editContent.trim() && !hasAutoGrammarCheckedRef.current) {
            hasAutoGrammarCheckedRef.current = true;
            // Small delay so the textarea renders with content before grammar check starts
            const timer = setTimeout(() => {
                handleRunGrammarCheck(true); // silent = true, no toast notifications
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [editContent, defaultEditing, isEditing, isReferencesSection]);

    const handleApplyFix = (originalWordOrIssue, suggestionParam, startParam, endParam) => {
        let originalWord = "";
        let suggestion = "";
        let start = 0;
        let end = 0;

        if (typeof originalWordOrIssue === "object" && originalWordOrIssue !== null) {
            start = originalWordOrIssue.start;
            end = originalWordOrIssue.end;
            suggestion = originalWordOrIssue.suggestion;
            originalWord = originalWordOrIssue.word;
        } else {
            originalWord = originalWordOrIssue;
            suggestion = suggestionParam;
            start = startParam;
            end = endParam;
        }

        if (!suggestion || start === undefined || end === undefined) return;

        const newContent = editContent.substring(0, start) + suggestion + editContent.substring(end);
        const lengthDiff = suggestion.length - (end - start);

        setEditContent(newContent);

        // Remove only the applied issue and shift character indices of subsequent issues
        const updated = grammarIssues
            .filter(i => !(i.start === start && i.end === end))
            .map(i => {
                if (i.start >= end) {
                    const newStart = i.start + lengthDiff;
                    const newEnd = i.end + lengthDiff;
                    return {
                        ...i,
                        start: newStart,
                        end: newEnd,
                        word: newContent.substring(newStart, newEnd)
                    };
                }
                return i;
            });

        setGrammarIssues(updated);
        setGrammarChecked(updated.length > 0);
        if (editorRef.current) {
            editorRef.current.focus();
            setTimeout(() => {
                setCaretCharacterOffsetWithin(editorRef.current, start + suggestion.length);
            }, 10);
        }
        toast.success(`Fixed: "${originalWord}" → "${suggestion}"`);
    };

    const renderTextWithRedUnderlines = (text, issues) => {
        if (!text) return null;
        if (!issues || issues.length === 0) return <span>{text}</span>;

        const elements = [];
        let lastIndex = 0;

        issues.forEach((issue, idx) => {
            if (issue.start > lastIndex) {
                elements.push(
                    <span key={`text-${lastIndex}`}>{text.substring(lastIndex, issue.start)}</span>
                );
            }

            const issueText = text.substring(issue.start, issue.end);

            elements.push(
                <span
                    key={`issue-${idx}-${issue.start}`}
                    style={{
                        textDecoration: 'underline wavy #dc3545',
                        textDecorationThickness: '2px',
                        backgroundColor: 'rgba(220, 53, 69, 0.12)',
                        color: '#b71c1c',
                        fontWeight: '600',
                        borderRadius: '2px',
                        padding: '1px 4px',
                        margin: '0 1px',
                        cursor: issue.suggestion ? 'pointer' : 'default',
                        display: 'inline-block'
                    }}
                    title={issue.message ? `${issue.message}${issue.suggestion ? ` (Click to fix: "${issue.suggestion}")` : ''}` : 'Grammar issue'}
                    onClick={() => {
                        if (issue.suggestion) {
                            handleApplyFix(issue);
                        }
                    }}
                >
                    {issueText}
                    {issue.suggestion && (
                        <span
                            style={{
                                fontSize: '11px',
                                fontWeight: 'normal',
                                color: '#198754',
                                backgroundColor: '#e8f5e9',
                                border: '1px solid #a5d6a7',
                                borderRadius: '3px',
                                padding: '0 4px',
                                marginLeft: '5px',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '2px'
                            }}
                        >
                            ✓ {issue.suggestion}
                        </span>
                    )}
                </span>
            );

            lastIndex = issue.end;
        });

        if (lastIndex < text.length) {
            elements.push(
                <span key={`text-end-${lastIndex}`}>{text.substring(lastIndex)}</span>
            );
        }

        return elements;
    };



    function handleRefranceCitation(content) {
        setTimeout(() => {
            var anchortags = document.querySelectorAll("a[href*='IDTAG=']");
            anchortags.forEach(function (tag) {
                const hrefData = tag.href.split('IDTAG=');
                tag.href = hrefData[0];
                var div = document.createElement("div");
                div.classList.add('refranceLinking');
                div.setAttribute("id", hrefData[1]);
                tag.appendChild(div);
            });
        }, "1000");
    }

    function removeEmptyTag(content) {
        if (!content || typeof content !== 'string') return content;

        const stripLeadingNumber = (text) => {
            let cleaned = text;
            let prev;
            do {
                prev = cleaned;
                cleaned = cleaned.replace(/^(\s*(?:<[^>]+>)*\s*)(?:\[\d+\]|\d+\.|\b\d+\b)\s*/gi, '$1');
            } while (cleaned !== prev);
            cleaned = cleaned.replace(/^<(b|strong|span|i|em)\b[^>]*><\/\1>/gi, '');
            return stripLeadingHeadingNumber(cleaned);
        };

        if (/<p[^>]*>/i.test(content)) {
            return content.replace(/(<p[^>]*>)([\s\S]*?)(<\/p>)/gi, (match, openTag, innerText, closeTag) => {
                return openTag + stripLeadingNumber(innerText) + closeTag;
            });
        } else if (/<li[^>]*>/i.test(content)) {
            return content.replace(/(<li[^>]*>)([\s\S]*?)(<\/li>)/gi, (match, openTag, innerText, closeTag) => {
                return openTag + stripLeadingNumber(innerText) + closeTag;
            });
        } else {
            return content
                .split('\n')
                .map(line => stripLeadingNumber(line))
                .join('\n');
        }
    }

    const renderFormattedDesc = () => {
        let content = details?.desc || '';
        if (details?.title === 'References') {
            content = removeEmptyTag(content);
        }
        let html = processContent(content, articleImages);
        if (profanityWords && Array.isArray(profanityWords) && profanityWords.length > 0) {
            profanityWords.forEach((word) => {
                if (word && typeof word === 'string' && word.trim() !== '') {
                    const safeWord = escapeRegExpMatch(word.trim());
                    html = html.replace(
                        new RegExp(`(<[^>]+>)|(\\b${safeWord}\\b)`, 'gi'),
                        (match, tag, wordMatch) =>
                            tag ? tag : `<strong style="color: red; background-color: #ffe6e6; padding: 2px 4px; border-radius: 3px; border: 1px solid #ffb3b3;">${wordMatch}</strong>`
                    );
                }
            });
        }
        return html;
    };

    const cleanedTitle = stripLeadingHeadingNumber(details?.title);

    return (
        <>
            <div id={`viewScroll${details?.title}`} className="commonContentScroll" />
            {cleanedTitle !== details?.title && (
                <div id={`viewScroll${cleanedTitle}`} className="commonContentScroll" />
            )}
            <div className={`contentPreview ${cleanedTitle === 'References' ? 'References' : ''}`} >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h2>{cleanedTitle}</h2>
                    {(isEditable || onSaveSection) && !isEditing && (
                        <button
                            type="button"
                            title="Edit Section"
                            onClick={() => {
                                setEditContent(convertHtmlToPlainText(details?.desc || ''));
                                setIsEditing(true);
                            }}
                            style={{
                                background: '#e6f4f8',
                                border: '1px solid #006f94',
                                color: '#006f94',
                                borderRadius: '6px',
                                padding: '5px 12px',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Edit
                        </button>
                    )}
                </div>

                {isEditing ? (
                    <div style={{ marginTop: '12px', marginBottom: '24px' }}>
                        {profaneWordsInEdit.length > 0 && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px 14px',
                                backgroundColor: '#fff2f2',
                                border: '1px solid #ffcdd2',
                                borderRadius: '6px',
                                fontSize: '13px',
                                color: '#c62828',
                                fontWeight: '600',
                                marginBottom: '10px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span>🚫 Profane word(s) detected in this section:</span>
                                    {profaneWordsInEdit.map((word, idx) => (
                                        <span
                                            key={`prof-badge-${idx}`}
                                            style={{
                                                color: '#d32f2f',
                                                backgroundColor: '#ffe6e6',
                                                border: '1px solid #ffcdd2',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                fontWeight: '700',
                                                fontSize: '12px',
                                                textDecoration: 'underline wavy #d32f2f'
                                            }}
                                        >
                                            {word}
                                        </span>
                                    ))}
                                    <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>(Please edit in text editor below to revise)</span>
                                </div>
                            </div>
                        )}
                        {grammarChecked && (
                            <div style={{
                                padding: '10px 14px',
                                backgroundColor: activeGrammarIssues.length > 0 ? '#fff2f2' : '#f0fff4',
                                border: `1px solid ${activeGrammarIssues.length > 0 ? '#ffcdd2' : '#c8e6c9'}`,
                                borderRadius: '6px 6px 0 0',
                                fontSize: '13px',
                                color: activeGrammarIssues.length > 0 ? '#c62828' : '#2e7d32',
                                fontWeight: '500',
                                marginBottom: '-1px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span>
                                        {activeGrammarIssues.length > 0
                                            ? `⚠️ Found ${activeGrammarIssues.length} grammar/spelling issue(s). Click suggestion to fix:`
                                            : `✅ Grammar Check complete: No issues found!`}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setGrammarChecked(false)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '14px', padding: '0 4px' }}
                                    >
                                        ✕
                                    </button>
                                </div>
                                {activeGrammarIssues.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                                        {activeGrammarIssues.map((issue, idx) => (
                                            <div
                                                key={`issue-pill-${idx}-${issue.start}`}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    backgroundColor: '#ffffff',
                                                    border: '1px solid #ffcdd2',
                                                    borderRadius: '4px',
                                                    padding: '3px 8px',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                <span style={{ textDecoration: 'line-through', color: '#c62828', fontWeight: '600' }}>{issue.word}</span>
                                                {issue.suggestion && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleApplyFix(issue)}
                                                        style={{
                                                            color: '#1b5e20',
                                                            backgroundColor: '#e8f5e9',
                                                            border: '1px solid #a5d6a7',
                                                            borderRadius: '3px',
                                                            padding: '2px 6px',
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer'
                                                        }}
                                                        title={`Replace "${issue.word}" with "${issue.suggestion}"`}
                                                    >
                                                        ✓ {issue.suggestion}
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={{ position: 'relative', width: '100%' }}>
                            <div
                                ref={editorRef}
                                contentEditable
                                suppressContentEditableWarning
                                onInput={(e) => {
                                    const plainText = e.currentTarget.innerText || e.currentTarget.textContent || '';
                                    setEditContent(plainText);
                                }}
                                onBlur={(e) => {
                                    const plainText = e.currentTarget.innerText || e.currentTarget.textContent || '';
                                    setEditContent(plainText);
                                }}
                                style={{
                                    width: '100%',
                                    minHeight: '180px',
                                    padding: '12px 12px 38px 12px',
                                    fontSize: '14px',
                                    borderRadius: (grammarChecked && activeGrammarIssues.length > 0) ? '0 0 6px 6px' : '6px',
                                    border: '1px solid #006f94',
                                    fontFamily: 'inherit',
                                    lineHeight: '1.6',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                    outline: 'none',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    backgroundColor: '#ffffff'
                                }}
                            />

                            <button
                                type="button"
                                title="Run Grammar Check for this section"
                                onClick={handleRunGrammarCheck}
                                disabled={isCheckingGrammar}
                                style={{
                                    position: 'absolute',
                                    bottom: '12px',
                                    right: '12px',
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    backgroundColor: '#dc3545',
                                    border: 'none',
                                    color: '#ffffff',
                                    cursor: isCheckingGrammar ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 6px rgba(220, 53, 69, 0.35)',
                                    transition: 'all 0.2s ease',
                                    zIndex: 5
                                }}
                                onMouseEnter={(e) => {
                                    if (!isCheckingGrammar) {
                                        e.currentTarget.style.transform = 'scale(1.1)';
                                        e.currentTarget.style.backgroundColor = '#bd2130';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isCheckingGrammar) {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.backgroundColor = '#dc3545';
                                    }
                                }}
                            >
                                {isCheckingGrammar ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px' }} />
                                ) : (
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                                        <path d="m9 12 2 2 4-4" />
                                    </svg>
                                )}
                            </button>
                        </div>

                        {(() => {
                            const processedDesc = processContent(details?.desc || '', articleImages);
                            const figures = [];
                            const figRegex = /<figure\b[^>]*>[\s\S]*?<\/figure>/gi;
                            let figMatch;
                            while ((figMatch = figRegex.exec(processedDesc)) !== null) {
                                figures.push(figMatch[0]);
                            }

                            const tables = [];
                            const tableRegex = /(?:<table-wrap\b[^>]*>[\s\S]*?<\/table-wrap>|<div\b[^>]*class="[^"]*table-responsive[^"]*"[^>]*>[\s\S]*?<\/div>|<table\b[^>]*>[\s\S]*?<\/table>)/gi;
                            let tableMatch;
                            while ((tableMatch = tableRegex.exec(processedDesc)) !== null) {
                                tables.push(tableMatch[0]);
                            }

                            return (
                                <>
                                    {figures.length > 0 && (
                                        <div style={{
                                            marginTop: '16px',
                                            padding: '16px',
                                            backgroundColor: '#f8fafc',
                                            border: '1px dashed #006f94',
                                            borderRadius: '8px',
                                        }}>
                                            <div style={{
                                                fontSize: '13px',
                                                fontWeight: '700',
                                                color: '#006f94',
                                                marginBottom: '12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                                    <polyline points="21 15 16 10 5 21" />
                                                </svg>
                                                Section Figures & Images ({figures.length}):
                                            </div>
                                            {figures.map((figHtml, idx) => (
                                                <div key={idx} dangerouslySetInnerHTML={{ __html: figHtml }} />
                                            ))}
                                        </div>
                                    )}

                                    {tables.length > 0 && (
                                        <div style={{
                                            marginTop: '16px',
                                            padding: '16px',
                                            backgroundColor: '#f8fafc',
                                            border: '1px dashed #006f94',
                                            borderRadius: '8px',
                                        }}>
                                            <div style={{
                                                fontSize: '13px',
                                                fontWeight: '700',
                                                color: '#006f94',
                                                marginBottom: '12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                                    <line x1="3" y1="9" x2="21" y2="9" />
                                                    <line x1="3" y1="15" x2="21" y2="15" />
                                                    <line x1="9" y1="3" x2="9" y2="21" />
                                                    <line x1="15" y1="3" x2="15" y2="21" />
                                                </svg>
                                                Section Tables ({tables.length}):
                                            </div>
                                            {tables.map((tableHtml, idx) => (
                                                <div key={idx} dangerouslySetInnerHTML={{ __html: tableHtml }} />
                                            ))}
                                        </div>
                                    )}
                                </>
                            );
                        })()}

                        {(() => {
                            const sectionImages = extractImagesFromHtml(details?.desc || '', articleImages);
                            if (sectionImages.length === 0) return null;

                            return (
                                <div style={{
                                    marginTop: '12px',
                                    padding: '12px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '6px',
                                }}>
                                    <div style={{
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: '#006f94',
                                        marginBottom: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                            <circle cx="8.5" cy="8.5" r="1.5" />
                                            <polyline points="21 15 16 10 5 21" />
                                        </svg>
                                        Images in this section ({sectionImages.length}):
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                        {sectionImages.map((imgSrc, idx) => (
                                            <div key={idx} style={{
                                                border: '1px solid #dee2e6',
                                                borderRadius: '6px',
                                                overflow: 'hidden',
                                                backgroundColor: '#fff',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                            }}>
                                                <img
                                                    src={imgSrc}
                                                    alt={`Section image ${idx + 1}`}
                                                    style={{
                                                        maxWidth: '300px',
                                                        maxHeight: '200px',
                                                        objectFit: 'contain',
                                                        display: 'block'
                                                    }}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}


                        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={isSaving}
                                onClick={async () => {
                                    if (onSaveSection) {
                                        setIsSaving(true);
                                        try {
                                            const formattedSaveContent = preserveNonTextHtmlBlocks(details?.desc, editContent);
                                            await onSaveSection(details?.title, formattedSaveContent, sectionIndex);
                                            setIsEditing(false);
                                            setGrammarChecked(false);
                                            setGrammarIssues([]);
                                        } catch (e) {
                                            console.error("Save section error:", e);
                                        } finally {
                                            setIsSaving(false);
                                        }
                                    } else {
                                        setIsEditing(false);
                                        setGrammarChecked(false);
                                        setGrammarIssues([]);
                                    }
                                }}
                                style={{
                                    backgroundColor: '#006f94',
                                    borderColor: '#006f94',
                                    color: '#ffffff',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    padding: '8px 22px',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                {isSaving ? "Saving..." : "Save Section"}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={isSaving}
                                onClick={() => {
                                    setEditContent(convertHtmlToPlainText(details?.desc || ''));
                                    setIsEditing(false);
                                    setGrammarChecked(false);
                                    setGrammarIssues([]);
                                }}
                                style={{
                                    backgroundColor: '#6c757d',
                                    borderColor: '#6c757d',
                                    color: '#ffffff',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    padding: '8px 22px',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        {renderFormattedDesc() && renderFormattedDesc().replace(/<[^>]+>/g, '').trim() !== '' ? (
                            <div dangerouslySetInnerHTML={{ __html: renderFormattedDesc() }} />
                        ) : (
                            <div style={{ color: "#888", fontStyle: "italic", fontSize: "14px", marginTop: "8px", padding: "6px 0" }}>
                                {isEditable ? "No content provided for this section. Click 'Edit' to add text." : "No content available."}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {cleanedTitle === 'References' ? handleRefranceCitation('') : ''}
        </>
    );
};

export default ContentDetails;


